(function () {
  const MAX_OPEN_TEMPLATES = 3;
  const TRANSCRIPT_BATCH_SIZE = 40;
  const MAX_TRANSCRIPT_BYTES = 8 * 1024 * 1024;
  const MANIFEST_CONTINUITY_TOLERANCE = 0.002;
  const SHOPIFY_AUDIO_MAX_BYTES = 20_000_000;
  const DRIVE_GATEWAY_PATH_PREFIX = '/intelligence-audio/';
  const AUDIO_EVENTS = [
    'loadstart',
    'loadedmetadata',
    'durationchange',
    'timeupdate',
    'play',
    'playing',
    'pause',
    'waiting',
    'stalled',
    'canplay',
    'seeked',
    'ended',
    'error'
  ];

  function defineIntelligenceDemo() {
    if (customElements.get('plaud-intelligence-demo')) return;

    class PlaudIntelligenceDemo extends PopupBase {
      constructor() {
        super();
        this.isInitialized = false;
        this.activeScenarioId = '';
        this.audioElements = [];
        this.audioStates = new WeakMap();
        this.transcriptStates = new WeakMap();
        this.transcriptStateList = new Set();
        this.transcriptObservers = new Map();
        this.summaryPanels = [];
        this.modalTrigger = null;
        this.modalCleanups = [];
        this.modalRemovalObserver = null;
        this.modalSequence = 0;
        this.focusRestoreTimer = null;
        this.templateFilterAnnouncementTimer = null;
        this.outlineFrame = null;

        this.handleClick = this.handleClick.bind(this);
        this.handleInput = this.handleInput.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleAudioEvent = this.handleAudioEvent.bind(this);
        this.handleSummaryScroll = this.handleSummaryScroll.bind(this);
      }

      connectedCallback() {
        if (this.isInitialized) return;

        this.isInitialized = true;
        this.audioElements = Array.from(this.querySelectorAll('[data-audio]'));
        this.summaryPanels = Array.from(this.querySelectorAll('.wwid__summary-panel'));

        this.addEventListener('click', this.handleClick);
        this.addEventListener('input', this.handleInput);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('keydown', this.handleKeyDown);
        this.addEventListener('focusout', this.handleFocusOut);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('scroll', this.handleViewportChange, true);
        window.addEventListener('resize', this.handleViewportChange);

        this.audioElements.forEach((audio) => {
          this.initializeAudio(audio);
          AUDIO_EVENTS.forEach((eventName) => {
            audio.addEventListener(eventName, this.handleAudioEvent);
          });
        });

        this.summaryPanels.forEach((panel) => {
          panel.addEventListener('scroll', this.handleSummaryScroll, { passive: true });
        });

        this.buildOutlines();
        this.updateScenarioOrientation();

        this.classList.add('is-enhanced');
        const deepLinkApplied = this.applyDeepLink();
        if (!deepLinkApplied) {
          const initialScenario = this.querySelector('[data-scenario-panel]:not([hidden])') || this.querySelector('[data-scenario-panel]');
          if (initialScenario) this.activateScenario(initialScenario.dataset.scenarioPanel, false);
        }
      }

      disconnectedCallback() {
        if (!this.isInitialized) return;

        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('input', this.handleInput);
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('keydown', this.handleKeyDown);
        this.removeEventListener('focusout', this.handleFocusOut);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('scroll', this.handleViewportChange, true);
        window.removeEventListener('resize', this.handleViewportChange);

        this.audioElements.forEach((audio) => {
          AUDIO_EVENTS.forEach((eventName) => {
            audio.removeEventListener(eventName, this.handleAudioEvent);
          });
          audio.pause();
          audio.removeAttribute('src');
          audio.load();
          const state = this.audioStates.get(audio);
          if (state && state.preloadAudio) {
            state.preloadAudio.removeAttribute('src');
            state.preloadAudio.load();
          }
        });

        this.transcriptStateList.forEach((state) => {
          if (state.abortController) state.abortController.abort();
          if (state.phase === 'loading') state.phase = 'idle';
          state.abortController = null;
          state.container.setAttribute('aria-busy', 'false');
        });
        this.transcriptStateList.clear();
        this.transcriptObservers.forEach((observer) => observer.disconnect());
        this.transcriptObservers.clear();

        this.summaryPanels.forEach((panel) => {
          panel.removeEventListener('scroll', this.handleSummaryScroll);
        });

        this.modalSequence += 1;
        this.modalTrigger = null;
        this.cleanupModalBindings();
        if (this.isModalOpen()) this.requestClose();
        if (this.modalRemovalObserver) this.modalRemovalObserver.disconnect();
        this.modalRemovalObserver = null;
        if (this.focusRestoreTimer) window.clearTimeout(this.focusRestoreTimer);
        this.focusRestoreTimer = null;
        if (this.templateFilterAnnouncementTimer) window.clearTimeout(this.templateFilterAnnouncementTimer);
        this.templateFilterAnnouncementTimer = null;
        if (this.outlineFrame) window.cancelAnimationFrame(this.outlineFrame);
        this.outlineFrame = null;
        this.modal = null;

        this.audioElements = [];
        this.summaryPanels = [];
        this.isInitialized = false;
      }

      handleClick(event) {
        const scenarioTrigger = event.target.closest('[data-scenario-trigger]');
        const outputTab = event.target.closest('[data-output-tab]');
        const menuTrigger = event.target.closest('[data-output-menu-trigger]');
        const pickerTrigger = event.target.closest('[data-open-template-picker]');
        const removeTemplateTrigger = event.target.closest('[data-remove-template]');
        const audioAction = event.target.closest('[data-audio-action]');
        const seekTrigger = event.target.closest('[data-seek]');
        const originalTrigger = event.target.closest('[data-open-original]');
        const outlineTrigger = event.target.closest('[data-outline-target]');
        const transcriptRetry = event.target.closest('[data-transcript-retry]');
        const transcriptLoadMore = event.target.closest('[data-transcript-load-more]');

        if (scenarioTrigger && this.contains(scenarioTrigger)) {
          this.activateScenario(scenarioTrigger.dataset.scenarioTrigger, false, true);
          return;
        }

        if (outputTab && this.contains(outputTab)) {
          const scenario = outputTab.closest('[data-scenario-panel]');
          this.activateOutput(scenario, outputTab.dataset.outputTab, false, true);
          return;
        }

        if (menuTrigger && this.contains(menuTrigger)) {
          this.toggleOutputMenu(menuTrigger);
          return;
        }

        if (removeTemplateTrigger && this.contains(removeTemplateTrigger)) {
          this.removeTemplateOutput(removeTemplateTrigger);
          return;
        }

        if (pickerTrigger && this.contains(pickerTrigger)) {
          this.closeOutputMenus();
          this.openTemplatePicker(pickerTrigger, pickerTrigger.dataset.openTemplatePicker || 'add');
          return;
        }

        if (audioAction && this.contains(audioAction)) {
          this.runAudioAction(audioAction);
          return;
        }

        if (transcriptRetry && this.contains(transcriptRetry)) {
          const scenario = transcriptRetry.closest('[data-scenario-panel]');
          this.retryTranscript(scenario);
          return;
        }

        if (transcriptLoadMore && this.contains(transcriptLoadMore)) {
          const scenario = transcriptLoadMore.closest('[data-scenario-panel]');
          this.renderNextTranscriptBatch(scenario);
          return;
        }

        if (seekTrigger && this.contains(seekTrigger)) {
          const scenario = seekTrigger.closest('[data-scenario-panel]');
          const audio = scenario ? scenario.querySelector('[data-audio]') : null;
          if (audio) this.seekAudio(audio, Number(seekTrigger.dataset.seek), true);
          return;
        }

        if (originalTrigger && this.contains(originalTrigger)) {
          this.openOriginalTranscript(originalTrigger);
          return;
        }

        if (outlineTrigger && this.contains(outlineTrigger)) {
          this.scrollToSummarySection(outlineTrigger);
        }
      }

      handleInput(event) {
        const progress = event.target.closest('[data-audio-progress]');
        if (!progress || !this.contains(progress)) return;

        const scenario = progress.closest('[data-scenario-panel]');
        const audio = scenario ? scenario.querySelector('[data-audio]') : null;
        if (!audio) return;

        progress.dataset.scrubbing = 'true';
        scenario.classList.add('is-scrubbing');
        this.updateScrubPreview(audio, progress);
      }

      handleChange(event) {
        const progress = event.target.closest('[data-audio-progress]');
        if (progress && this.contains(progress)) {
          const scenario = progress.closest('[data-scenario-panel]');
          const audio = scenario ? scenario.querySelector('[data-audio]') : null;
          if (!audio) return;

          const wasScrubbing = progress.dataset.scrubbing === 'true';
          delete progress.dataset.scrubbing;
          scenario.classList.remove('is-scrubbing');
          if (wasScrubbing) this.seekAudio(audio, Number(progress.value), false);
          return;
        }

        const speed = event.target.closest('[data-audio-speed]');
        if (!speed || !this.contains(speed)) return;

        const scenario = speed.closest('[data-scenario-panel]');
        const audio = scenario ? scenario.querySelector('[data-audio]') : null;
        const playbackRate = Number(speed.value);
        if (!audio || !Number.isFinite(playbackRate)) return;

        const speedLabel = speed.parentElement ? speed.parentElement.querySelector('[data-speed-label]') : null;
        if (speedLabel) speedLabel.textContent = speed.options[speed.selectedIndex].text;
        const audioState = this.audioStates.get(audio);
        if (audioState) audioState.playbackRate = playbackRate;
        audio.playbackRate = playbackRate;
        this.announce(`Playback speed set to ${speed.options[speed.selectedIndex].text}.`);
      }

      handleKeyDown(event) {
        const tab = event.target.closest('[role="tab"], [data-output-tab]');
        const menu = event.target.closest('[data-output-menu-trigger], [role="menu"]');
        const menuContainer = event.target.closest('[role="menu"]');

        if (event.key === 'Escape' && menu) {
          this.closeOutputMenus();
          const actionContainer = menu.closest('[data-active-template-actions], [data-output-tab-group]');
          const trigger = actionContainer ? actionContainer.querySelector('[data-output-menu-trigger]') : null;
          if (trigger) trigger.focus();
          return;
        }

        if (menuContainer && ['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
          const items = Array.from(menuContainer.querySelectorAll('[role="menuitem"]')).filter((item) => !item.hidden && !item.disabled);
          const currentIndex = items.indexOf(event.target.closest('[role="menuitem"]'));
          if (!items.length || currentIndex < 0) return;

          let nextIndex = currentIndex;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = items.length - 1;
          if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length;
          if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length;
          event.preventDefault();
          items[nextIndex].focus();
          return;
        }

        if (!tab || !this.contains(tab)) return;

        const isScenarioTab = tab.hasAttribute('data-scenario-trigger');
        const isHorizontal = isScenarioTab && tab.closest('[role="tablist"]')?.getAttribute('aria-orientation') === 'horizontal';
        const supportedKeys = isScenarioTab
          ? [isHorizontal ? 'ArrowLeft' : 'ArrowUp', isHorizontal ? 'ArrowRight' : 'ArrowDown', 'Home', 'End']
          : ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!supportedKeys.includes(event.key)) return;

        const tablist = tab.closest('[role="tablist"]');
        if (!tablist) return;

        const tabSelector = tab.hasAttribute('data-output-tab') ? '[data-output-tab]' : '[role="tab"]';
        const tabs = Array.from(tablist.querySelectorAll(tabSelector)).filter((item) => {
          const group = item.closest('[data-output-tab-group]');
          return item.closest('[role="tablist"]') === tablist && (!group || !group.hidden);
        });
        const currentIndex = tabs.indexOf(tab);
        if (currentIndex < 0 || tabs.length < 2) return;

        let nextIndex = currentIndex;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;

        event.preventDefault();
        const nextTab = tabs[nextIndex];
        if (nextTab.hasAttribute('data-scenario-trigger')) {
          this.activateScenario(nextTab.dataset.scenarioTrigger, true, true);
        } else {
          const scenario = nextTab.closest('[data-scenario-panel]');
          this.activateOutput(scenario, nextTab.dataset.outputTab, false, true);
        }
        nextTab.focus();
      }

      handleDocumentClick(event) {
        if (this.contains(event.target) && event.target.closest('[data-output-tab-group], [data-active-template-actions]')) return;
        this.closeOutputMenus();
      }

      handleFocusOut(event) {
        const progress = event.target.closest('[data-audio-progress][data-scrubbing="true"]');
        if (progress && !progress.contains(event.relatedTarget)) {
          const scenario = progress.closest('[data-scenario-panel]');
          const audio = scenario ? scenario.querySelector('[data-audio]') : null;
          delete progress.dataset.scrubbing;
          if (scenario) scenario.classList.remove('is-scrubbing');
          if (audio) this.seekAudio(audio, Number(progress.value), false);
        }

        const actionContainer = event.target.closest('.is-menu-open');
        if (!actionContainer || actionContainer.contains(event.relatedTarget)) return;

        window.requestAnimationFrame(() => {
          if (!actionContainer.contains(document.activeElement)) this.closeOutputMenus();
        });
      }

      updateScenarioOrientation() {
        const rail = this.querySelector('.wwid__scenario-rail');
        if (!rail) return;

        rail.setAttribute('aria-orientation', 'horizontal');
      }

      handleViewportChange(event) {
        this.closeOutputMenus();
        if (!event || event.type === 'resize') this.updateScenarioOrientation();
        if (this.outlineFrame) return;
        this.outlineFrame = window.requestAnimationFrame(() => {
          this.outlineFrame = null;
          this.summaryPanels.forEach((panel) => {
            if (!panel.hidden && panel.classList.contains('is-active')) this.updateOutline(panel);
          });
        });
      }

      handleBlockSelect(event) {
        if (!event.detail || !event.detail.blockId) return;
        const trigger = this.querySelector(`[data-scenario-trigger="${event.detail.blockId}"]`);
        if (!trigger) return;

        this.activateScenario(event.detail.blockId, true, true);
        trigger.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }

      activateScenario(scenarioId, resetAudio, syncUrl) {
        const nextPanel = this.querySelector(`[data-scenario-panel="${scenarioId}"]`);
        if (!nextPanel) return;

        this.querySelectorAll('[data-scenario-trigger]').forEach((trigger) => {
          const isActive = trigger.dataset.scenarioTrigger === scenarioId;
          trigger.classList.toggle('is-active', isActive);
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
          trigger.tabIndex = isActive ? 0 : -1;
        });

        this.querySelectorAll('[data-scenario-panel]').forEach((panel) => {
          const isActive = panel === nextPanel;
          if (!isActive && !panel.hidden) this.resetScenarioAudio(panel, resetAudio);
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        });

        this.activeScenarioId = scenarioId;
        this.activateOutput(nextPanel, nextPanel.dataset.activeOutput || 'transcript', false);
        this.updateAddTemplateButton(nextPanel);
        this.ensureTranscript(nextPanel);
        if (syncUrl) this.syncUrlState(nextPanel, nextPanel.dataset.activeOutput || 'transcript');

        const audio = nextPanel.querySelector('[data-audio]');
        if (audio) {
          this.updateAudioUI(audio);
        }
      }

      activateOutput(scenario, outputKey, focusTab, syncUrl) {
        if (!scenario) return;

        const selectedTab = scenario.querySelector(`[data-output-tab="${outputKey}"]`);
        const selectedPanel = scenario.querySelector(`[data-output-panel="${outputKey}"]`);
        const selectedGroup = selectedTab ? selectedTab.closest('[data-output-tab-group]') : null;
        if (!selectedTab || !selectedPanel || (selectedGroup && selectedGroup.hidden)) return;

        this.closeOutputMenus();
        scenario.querySelectorAll('[data-output-tab]').forEach((tab) => {
          const isActive = tab === selectedTab;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
          const group = tab.closest('[data-output-tab-group]');
          if (group) group.classList.toggle('is-active', isActive);
        });

        scenario.querySelectorAll('[data-output-panel]').forEach((panel) => {
          const isActive = panel === selectedPanel;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
        });

        if (selectedPanel.classList.contains('wwid__summary-panel')) {
          selectedPanel.scrollTop = 0;
          this.updateOutline(selectedPanel);
        }

        scenario.dataset.activeOutput = outputKey;
        if (outputKey === 'transcript') this.ensureTranscript(scenario);
        if (syncUrl) this.syncUrlState(scenario, outputKey);
        if (focusTab) selectedTab.focus();
      }

      toggleOutputMenu(trigger) {
        const actionContainer = trigger.closest('[data-active-template-actions], [data-output-tab-group]');
        const menu = actionContainer ? actionContainer.querySelector('[role="menu"]') : null;
        if (!menu) return;

        const willOpen = menu.hidden;
        this.closeOutputMenus();
        menu.hidden = !willOpen;
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        actionContainer.classList.toggle('is-menu-open', willOpen);

        if (willOpen) {
          const triggerRect = trigger.getBoundingClientRect();
          const menuWidth = menu.offsetWidth;
          const menuLeft = Math.min(
            Math.max(8, triggerRect.right - menuWidth),
            Math.max(8, window.innerWidth - menuWidth - 8)
          );
          const menuHeight = menu.offsetHeight;
          const spaceBelow = window.innerHeight - triggerRect.bottom - 8;
          const menuTop = spaceBelow >= menuHeight + 6
            ? triggerRect.bottom + 6
            : Math.max(8, triggerRect.top - menuHeight - 6);
          const maxMenuTop = Math.max(8, window.innerHeight - menuHeight - 8);
          menu.style.top = `${Math.max(8, Math.min(menuTop, maxMenuTop))}px`;
          menu.style.left = `${menuLeft}px`;
          const firstItem = Array.from(menu.querySelectorAll('[role="menuitem"]')).find((item) => {
            return !item.hidden && !item.disabled && !item.closest('[hidden]');
          });
          if (firstItem) firstItem.focus();
        }
      }

      closeOutputMenus() {
        this.querySelectorAll('[data-output-menu-trigger]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });
        this.querySelectorAll('.wwid__output-menu').forEach((menu) => {
          menu.hidden = true;
          menu.style.removeProperty('top');
          menu.style.removeProperty('left');
          const actionContainer = menu.closest('[data-active-template-actions], [data-output-tab-group]');
          if (actionContainer) actionContainer.classList.remove('is-menu-open');
        });
      }

      removeTemplateOutput(trigger) {
        const scenario = trigger.closest('[data-scenario-panel]');
        const group = trigger.closest('[data-template-tab]') || (scenario ? scenario.querySelector('[data-template-tab].is-active:not([hidden])') : null);
        const tab = group ? group.querySelector('[data-output-tab]') : null;
        const outputKey = group ? group.dataset.outputTabGroup : '';
        const panel = scenario && outputKey ? scenario.querySelector(`[data-output-panel="${outputKey}"]`) : null;
        if (!group || !scenario || !tab || !panel) return;

        const visibleGroups = Array.from(scenario.querySelectorAll('[data-output-tab-group]:not([hidden])'));
        const currentIndex = visibleGroups.indexOf(group);
        const fallbackGroup = visibleGroups[Math.max(0, currentIndex - 1)] || scenario.querySelector('[data-output-tab-group="summary"]');
        const fallbackTab = fallbackGroup ? fallbackGroup.querySelector('[data-output-tab]') : null;
        const wasActive = group.classList.contains('is-active');
        const label = tab.textContent.trim();

        this.closeOutputMenus();
        group.hidden = true;
        group.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
        panel.hidden = true;
        panel.classList.remove('is-active');
        const outputTabs = scenario.querySelector('.wwid__output-tabs');
        if (outputTabs) outputTabs.appendChild(group);

        if (wasActive && fallbackTab) {
          this.activateOutput(scenario, fallbackTab.dataset.outputTab, true, true);
        } else {
          const addButton = scenario.querySelector('[data-add-template]');
          if (addButton) window.requestAnimationFrame(() => addButton.focus());
        }

        this.updateAddTemplateButton(scenario);
        this.announce(`${label} template removed.`);
      }

      commitTemplateSelection(scenario, outputKey, mode, replaceOutputKey) {
        const selectedGroup = scenario ? scenario.querySelector(`[data-template-tab][data-output-tab-group="${outputKey}"]`) : null;
        const selectedTab = selectedGroup ? selectedGroup.querySelector('[data-output-tab]') : null;
        const outputTabs = scenario ? scenario.querySelector('.wwid__output-tabs') : null;
        if (!scenario || !selectedGroup || !selectedTab || !outputTabs) return null;

        const openGroups = Array.from(scenario.querySelectorAll('[data-template-tab]:not([hidden])'));
        if (mode !== 'replace' && selectedGroup.hidden && openGroups.length >= MAX_OPEN_TEMPLATES) return null;

        if (mode === 'replace') {
          const replacedGroup = scenario.querySelector(`[data-template-tab][data-output-tab-group="${replaceOutputKey}"]`);
          if (!replacedGroup || replacedGroup.hidden) return null;
          if (replacedGroup !== selectedGroup) {
            replacedGroup.before(selectedGroup);
            replacedGroup.hidden = true;
            replacedGroup.classList.remove('is-active');
            const replacedTab = replacedGroup.querySelector('[data-output-tab]');
            const replacedPanel = scenario.querySelector(`[data-output-panel="${replaceOutputKey}"]`);
            if (replacedTab) {
              replacedTab.setAttribute('aria-selected', 'false');
              replacedTab.tabIndex = -1;
            }
            if (replacedPanel) {
              replacedPanel.hidden = true;
              replacedPanel.classList.remove('is-active');
            }
            outputTabs.appendChild(replacedGroup);
          }
        } else if (selectedGroup.hidden) {
          const addButton = scenario.querySelector('[data-add-template]');
          outputTabs.insertBefore(selectedGroup, addButton || null);
        }

        selectedGroup.hidden = false;
        this.activateOutput(scenario, outputKey, false, true);
        this.updateAddTemplateButton(scenario);
        return selectedTab;
      }

      updateAddTemplateButton(scenario) {
        const addButton = scenario ? scenario.querySelector('[data-add-template]') : null;
        const templateGroups = scenario ? Array.from(scenario.querySelectorAll('[data-template-tab]')) : [];
        if (!addButton) return;

        const openCount = templateGroups.filter((group) => !group.hidden).length;
        const allAdded = templateGroups.length === 0 || openCount === templateGroups.length;
        const isReplaceMode = !allAdded && openCount >= MAX_OPEN_TEMPLATES;
        const label = allAdded
          ? 'All available summary templates added'
          : isReplaceMode
            ? 'Replace a summary template'
            : 'Add a summary template';
        addButton.hidden = templateGroups.length === 0;
        addButton.disabled = allAdded;
        addButton.dataset.openTemplatePicker = isReplaceMode ? 'replace-select' : 'add';
        addButton.setAttribute('aria-disabled', allAdded ? 'true' : 'false');
        addButton.setAttribute('aria-label', label);
        addButton.title = label;

        scenario.querySelectorAll('[data-open-template-picker="replace"]').forEach((button) => {
          button.disabled = templateGroups.length <= openCount;
        });
      }

      applyDeepLink() {
        const url = new URL(window.location.href);
        const hasScenarioRequest = url.searchParams.has('scenario');
        const hasTemplateRequest = url.searchParams.has('template');
        const requestedScenarioKey = this.publicKey(url.searchParams.get('scenario'));
        const requestedTemplateHandle = this.publicKey(url.searchParams.get('template'));
        if (!hasScenarioRequest && !hasTemplateRequest) return false;

        const panels = Array.from(this.querySelectorAll('[data-scenario-panel]'));
        const requestedScenario = requestedScenarioKey
          ? panels.find((panel) => panel.dataset.scenarioKey === requestedScenarioKey)
          : null;
        const scenario = requestedScenario || panels[0];
        if (!scenario) return false;

        this.activateScenario(scenario.dataset.scenarioPanel, false);
        this.activateOutput(scenario, 'transcript', false);

        if (hasScenarioRequest && (!requestedScenarioKey || !requestedScenario)) {
          this.syncUrlState(scenario, 'transcript');
          this.announce('The requested conversation sample is unavailable. Showing the first conversation.');
          return true;
        }

        if (!hasTemplateRequest) {
          this.syncUrlState(scenario, 'transcript');
          return true;
        }

        if (!requestedTemplateHandle) {
          this.syncUrlState(scenario, 'transcript');
          this.announce('That template is not available for this conversation. Showing the transcript.');
          return true;
        }

        const group = Array.from(scenario.querySelectorAll('[data-template-tab]')).find((item) => {
          return item.dataset.templateHandle === requestedTemplateHandle;
        });
        const outputKey = group ? group.dataset.outputTabGroup : '';
        const outputTab = outputKey ? this.commitTemplateSelection(scenario, outputKey, 'add', '') : null;
        if (outputTab) {
          this.announce(`${outputTab.textContent.trim()} template sample opened.`);
          return true;
        }

        this.syncUrlState(scenario, 'transcript');
        this.announce('That template is not available for this conversation. Showing the transcript.');
        return true;
      }

      syncUrlState(scenario, outputKey) {
        if (!scenario || (window.Shopify && window.Shopify.designMode)) return;
        const scenarioKey = this.publicKey(scenario.dataset.scenarioKey);
        if (!scenarioKey || typeof window.history.replaceState !== 'function') return;

        const url = new URL(window.location.href);
        url.searchParams.set('scenario', scenarioKey);
        const group = outputKey ? scenario.querySelector(`[data-output-tab-group="${outputKey}"]`) : null;
        const templateHandle = group ? this.publicKey(group.dataset.templateHandle) : '';
        if (templateHandle) {
          url.searchParams.set('template', templateHandle);
        } else {
          url.searchParams.delete('template');
        }
        window.history.replaceState(window.history.state, '', url.toString());
      }

      publicKey(value) {
        const key = String(value || '').trim().toLowerCase();
        return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(key) ? key : '';
      }

      initializeAudio(audio) {
        const scenario = audio.closest('[data-scenario-panel]');
        const manifestNode = scenario ? scenario.querySelector('[data-audio-manifest]') : null;
        const fallbackDuration = Number(audio.dataset.durationFallback);
        let parts = [];
        let duration = Number.isFinite(fallbackDuration) && fallbackDuration > 0 ? fallbackDuration : 0;
        let manifestError = '';

        try {
          if (manifestNode) {
            const manifest = JSON.parse(manifestNode.textContent);
            if (![1, 2].includes(manifest.schemaVersion)) throw new Error('Audio manifest schema version is unsupported.');
            if (String(manifest.scenario || '') !== String(scenario.dataset.scenarioKey || '')) {
              throw new Error('Audio manifest scenario does not match this recording.');
            }
            if (String(manifest.contentVersion || '') !== String(scenario.dataset.contentVersion || '')) {
              throw new Error('Audio manifest version does not match this recording.');
            }
            if (!Array.isArray(manifest.parts) || !manifest.parts.length) throw new Error('Audio manifest has no parts.');
            parts = manifest.parts.map((part, index) => {
              const manifestIndex = Number(part.index);
              const start = Number(part.start);
              const partDuration = Number(part.duration);
              const mediaStart = Number(part.mediaStart || 0);
              const provider = manifest.schemaVersion === 1
                ? 'shopify_file'
                : String(part.provider || '');
              const src = this.normalizeMediaUrl(part.src, provider);
              if (
                !Number.isInteger(manifestIndex)
                || manifestIndex !== index + 1
                || !src
                || !Number.isFinite(start)
                || start < 0
                || !Number.isFinite(partDuration)
                || partDuration <= 0
                || !Number.isFinite(mediaStart)
                || mediaStart < 0
              ) {
                throw new Error(`Audio manifest part ${index + 1} is invalid.`);
              }
              if (manifest.schemaVersion === 2) {
                const byteSize = Number(part.byteSize);
                const checksum = String(part.checksum || '');
                const sourceRevision = String(part.sourceRevision || '');
                if (
                  !['shopify_file', 'google_drive_gateway'].includes(provider)
                  || String(part.scenario || '') !== String(manifest.scenario || '')
                  || String(part.contentVersion || '') !== String(manifest.contentVersion || '')
                  || !Number.isSafeInteger(byteSize)
                  || byteSize <= 0
                  || !/^sha256:[a-f0-9]{64}$/.test(checksum)
                  || (provider === 'shopify_file' && byteSize >= SHOPIFY_AUDIO_MAX_BYTES)
                  || (provider === 'google_drive_gateway'
                    && !/^[A-Za-z0-9_-]{1,200}$/.test(sourceRevision))
                ) {
                  throw new Error(`Audio manifest part ${index + 1} has invalid provider or version metadata.`);
                }
              }
              return {
                index,
                start,
                duration: partDuration,
                mediaStart,
                provider,
                src,
              };
            });
            parts.forEach((part, index) => {
              if (index === 0 && Math.abs(part.start) > MANIFEST_CONTINUITY_TOLERANCE) throw new Error('Audio manifest must start at zero.');
              if (index === 0) return;
              const previousEnd = parts[index - 1].start + parts[index - 1].duration;
              if (Math.abs(previousEnd - part.start) > MANIFEST_CONTINUITY_TOLERANCE) {
                throw new Error(`Audio manifest has a gap or overlap before part ${index + 1}.`);
              }
            });
            const configuredDuration = Number(manifest.duration);
            const finalEnd = parts[parts.length - 1].start + parts[parts.length - 1].duration;
            if (
              Number.isFinite(configuredDuration)
              && configuredDuration > 0
              && Math.abs(configuredDuration - finalEnd) > MANIFEST_CONTINUITY_TOLERANCE
            ) {
              throw new Error('Audio manifest duration does not match its parts.');
            }
            duration = Number.isFinite(configuredDuration) && configuredDuration > 0 ? configuredDuration : finalEnd;
          } else if (audio.dataset.audioSrc) {
            const src = this.normalizeMediaUrl(audio.dataset.audioSrc, 'shopify_file');
            if (!src) throw new Error('Audio URL is invalid.');
            parts = [{ index: 0, start: 0, duration, mediaStart: 0, provider: 'shopify_file', src }];
          }
        } catch (error) {
          manifestError = error instanceof Error ? error.message : 'Audio manifest is invalid.';
          parts = [];
        }

        const state = {
          parts,
          duration,
          globalTime: 0,
          currentPartIndex: -1,
          pendingGlobalTime: null,
          pendingPlay: false,
          playIntent: false,
          playbackRate: 1,
          completed: false,
          preloadAudio: null,
          preloadedPartIndex: -1,
          manifestError,
        };
        this.audioStates.set(audio, state);
        if (!parts.length) {
          this.disableAudio(audio, manifestError || 'Sample audio is unavailable.');
        }
      }

      normalizeMediaUrl(value, provider = 'shopify_file') {
        if (typeof value !== 'string' || !value.trim()) return '';
        try {
          const url = new URL(value, window.location.href);
          if (url.protocol !== 'https:' || url.username || url.password || url.hash) return '';
          const hostname = url.hostname.toLowerCase();
          if (provider === 'shopify_file') {
            const storefrontHost = new URL(window.location.href).hostname.toLowerCase();
            const isShopifyHost = hostname === 'cdn.shopify.com'
              || hostname.endsWith('.shopifycdn.net')
              || hostname.endsWith('.myshopify.com')
              || hostname === storefrontHost;
            return isShopifyHost ? url.href : '';
          }
          if (provider === 'google_drive_gateway') {
            const gatewayHost = String(this.dataset?.audioGatewayHost || '').trim().toLowerCase();
            const signature = url.searchParams.get('sig') || '';
            const hasOnlySignature = Array.from(url.searchParams.keys()).every((key) => key === 'sig');
            if (
              !/^[a-z0-9.-]+$/.test(gatewayHost)
              || hostname !== gatewayHost
              || !url.pathname.startsWith(DRIVE_GATEWAY_PATH_PREFIX)
              || !/^[a-f0-9]{64}$/.test(signature)
              || !hasOnlySignature
            ) {
              return '';
            }
            return url.href;
          }
          return '';
        } catch (error) {
          return '';
        }
      }

      runAudioAction(trigger) {
        const scenario = trigger.closest('[data-scenario-panel]');
        const audio = scenario ? scenario.querySelector('[data-audio]') : null;
        const state = audio ? this.audioStates.get(audio) : null;
        if (!audio || !state || trigger.disabled) return;

        const action = trigger.dataset.audioAction;
        if (action === 'retry') {
          this.retryAudio(audio);
          return;
        }

        if (action === 'toggle') {
          if (state.playIntent) {
            this.pauseAudio(audio);
          } else {
            this.playAudio(audio);
          }
          return;
        }

        if (action === 'back') {
          this.seekAudio(audio, Math.max(0, state.globalTime - 15), false);
          return;
        }

        if (action === 'forward') {
          this.seekAudio(audio, Math.min(state.duration, state.globalTime + 15), false);
        }
      }

      findAudioPartIndex(state, requestedTime) {
        if (!state.parts.length) return -1;
        const time = Math.max(0, Math.min(state.duration, requestedTime));
        let low = 0;
        let high = state.parts.length - 1;
        let result = 0;
        while (low <= high) {
          const middle = Math.floor((low + high) / 2);
          if (state.parts[middle].start <= time) {
            result = middle;
            low = middle + 1;
          } else {
            high = middle - 1;
          }
        }
        return result;
      }

      ensureAudioSource(audio, requestedTime, playAfterLoad) {
        const state = this.audioStates.get(audio);
        if (!state || !state.parts.length) return false;
        const targetTime = Number.isFinite(requestedTime) ? requestedTime : state.globalTime;
        const partIndex = this.findAudioPartIndex(state, targetTime);
        const part = state.parts[partIndex];
        if (!part) return false;

        if (state.currentPartIndex === partIndex && audio.getAttribute('src') && this.normalizeMediaUrl(audio.currentSrc, part.provider) === part.src) {
          return true;
        }

        this.loadAudioPart(audio, partIndex, targetTime, Boolean(playAfterLoad));
        return false;
      }

      loadAudioPart(audio, partIndex, globalTime, playAfterLoad) {
        const state = this.audioStates.get(audio);
        const part = state ? state.parts[partIndex] : null;
        if (!state || !part) return;

        state.currentPartIndex = partIndex;
        state.globalTime = Math.max(part.start, Math.min(part.start + part.duration, globalTime));
        state.pendingGlobalTime = state.globalTime;
        state.pendingPlay = Boolean(playAfterLoad);
        if (playAfterLoad) state.playIntent = true;
        state.completed = false;
        audio.pause();
        audio.preload = 'metadata';
        if (part.provider === 'google_drive_gateway') audio.crossOrigin = 'anonymous';
        audio.src = part.src;
        audio.playbackRate = state.playbackRate;
        this.setAudioLoading(audio, true);
        audio.load();
        this.updateAudioUI(audio);
      }

      setAudioLocalTime(audio, globalTime) {
        const state = this.audioStates.get(audio);
        const part = state ? state.parts[state.currentPartIndex] : null;
        if (!state || !part) return false;
        const logicalOffset = Math.max(0, Math.min(part.duration, globalTime - part.start));
        const localTime = part.mediaStart + logicalOffset;
        try {
          audio.currentTime = localTime;
          state.globalTime = part.start + logicalOffset;
          return true;
        } catch (error) {
          return false;
        }
      }

      playAudio(audio) {
        const state = this.audioStates.get(audio);
        const scenario = audio.closest('[data-scenario-panel]');
        if (!state || !state.parts.length || !scenario || scenario.hidden) return;

        this.pauseOtherAudio(audio);
        if (state.completed || state.globalTime >= state.duration - 0.05) {
          state.globalTime = 0;
          state.completed = false;
        }
        state.playIntent = true;

        if (!this.ensureAudioSource(audio, state.globalTime, true)) return;
        if (audio.readyState < 2) {
          state.pendingGlobalTime = state.globalTime;
          state.pendingPlay = true;
          this.setAudioLoading(audio, true);
          return;
        }
        this.startLoadedAudio(audio);
      }

      startLoadedAudio(audio) {
        const state = this.audioStates.get(audio);
        if (!state) return;
        state.pendingPlay = false;
        state.playIntent = true;
        audio.playbackRate = state.playbackRate;
        const playResult = audio.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch((error) => {
            if (error && error.name === 'AbortError') return;
            state.playIntent = false;
            this.setAudioLoading(audio, false);
            this.showAudioStatus(audio, 'Playback could not continue. Select Play to resume.', true);
            this.updateAudioUI(audio);
          });
        }
      }

      pauseAudio(audio) {
        const state = this.audioStates.get(audio);
        if (!state) return;
        this.updateAudioGlobalTime(audio);
        state.playIntent = false;
        state.pendingPlay = false;
        audio.pause();
        this.setAudioLoading(audio, false);
        this.updateAudioUI(audio);
      }

      pauseOtherAudio(activeAudio) {
        this.audioElements.forEach((audio) => {
          if (audio !== activeAudio) this.pauseAudio(audio);
        });
      }

      resetScenarioAudio(scenario, resetAudio) {
        const audio = scenario.querySelector('[data-audio]');
        const state = audio ? this.audioStates.get(audio) : null;
        if (!audio || !state) return;

        this.updateAudioGlobalTime(audio);
        state.playIntent = false;
        state.pendingPlay = false;
        state.pendingGlobalTime = null;
        state.completed = false;
        if (resetAudio) state.globalTime = 0;
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        state.currentPartIndex = -1;
        this.releaseAudioPreload(state);
        this.setAudioLoading(audio, false);
        this.updateAudioUI(audio);
        this.updateCurrentSegment(audio, resetAudio ? -1 : state.globalTime);
      }

      seekAudio(audio, requestedTime, playAfterSeek) {
        const state = this.audioStates.get(audio);
        if (!state || !state.parts.length || !Number.isFinite(requestedTime)) return;

        const targetTime = Math.max(0, Math.min(state.duration, requestedTime));
        const shouldPlay = Boolean(playAfterSeek || state.playIntent);
        const targetPartIndex = this.findAudioPartIndex(state, targetTime);
        state.globalTime = targetTime;
        state.completed = false;
        state.playIntent = shouldPlay;

        if (state.currentPartIndex !== targetPartIndex || !audio.getAttribute('src')) {
          this.loadAudioPart(audio, targetPartIndex, targetTime, shouldPlay);
          return;
        }

        if (!this.setAudioLocalTime(audio, targetTime)) {
          state.pendingGlobalTime = targetTime;
          state.pendingPlay = shouldPlay;
          return;
        }

        this.updateAudioUI(audio);
        if (shouldPlay) this.startLoadedAudio(audio);
      }

      updateAudioGlobalTime(audio) {
        const state = this.audioStates.get(audio);
        const part = state ? state.parts[state.currentPartIndex] : null;
        if (!state || !part || !Number.isFinite(audio.currentTime)) return state ? state.globalTime : 0;
        const logicalOffset = Math.max(0, Math.min(part.duration, audio.currentTime - part.mediaStart));
        state.globalTime = Math.max(0, Math.min(state.duration, part.start + logicalOffset));
        return state.globalTime;
      }

      handleAudioEvent(event) {
        const audio = event.currentTarget;
        const state = this.audioStates.get(audio);
        const scenario = audio.closest('[data-scenario-panel]');
        if (!state) return;
        let playPending = false;

        if (event.type === 'loadedmetadata') {
          const part = state.parts[state.currentPartIndex];
          if (part && state.parts.length === 1 && part.duration <= 0 && Number.isFinite(audio.duration)) {
            part.duration = Math.max(0, audio.duration - part.mediaStart);
            state.duration = part.duration;
          }
          if (state.pendingGlobalTime !== null) {
            const pendingTime = state.pendingGlobalTime;
            state.pendingGlobalTime = null;
            if (!this.setAudioLocalTime(audio, pendingTime)) {
              this.showAudioStatus(audio, 'This recording could not seek to the selected moment.', true);
            }
          }
          playPending = state.pendingPlay && Boolean(scenario && !scenario.hidden && scenario.classList.contains('is-active'));
        }

        if (event.type === 'error') {
          state.playIntent = false;
          state.pendingPlay = false;
          this.disableAudio(audio, 'This recording part could not be loaded.');
          return;
        }

        if (event.type === 'ended') {
          const nextPartIndex = state.currentPartIndex + 1;
          if (nextPartIndex < state.parts.length && state.playIntent) {
            const nextPart = state.parts[nextPartIndex];
            this.loadAudioPart(audio, nextPartIndex, nextPart.start, true);
            return;
          }
          state.globalTime = state.duration;
          state.playIntent = false;
          state.pendingPlay = false;
          state.completed = true;
        } else if (event.type === 'pause' && !audio.ended && !state.pendingPlay) {
          state.playIntent = false;
        }

        if (['timeupdate', 'play', 'playing', 'pause', 'seeked', 'durationchange'].includes(event.type)) {
          this.updateAudioGlobalTime(audio);
        }
        if (['play', 'playing'].includes(event.type)) state.playIntent = true;

        if (['loadstart', 'waiting', 'stalled'].includes(event.type)) {
          this.setAudioLoading(audio, true);
        }
        if (['loadedmetadata', 'canplay', 'playing', 'seeked'].includes(event.type)) {
          this.setAudioLoading(audio, false);
          this.hideAudioStatus(audio);
        }
        if (['pause', 'ended'].includes(event.type)) this.setAudioLoading(audio, false);

        this.maybePreloadNextAudioPart(audio);
        this.updateAudioUI(audio);
        if (playPending) this.startLoadedAudio(audio);
      }

      maybePreloadNextAudioPart(audio) {
        const state = this.audioStates.get(audio);
        const part = state ? state.parts[state.currentPartIndex] : null;
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!state || !part || connection?.saveData) return;
        const nextPartIndex = state.currentPartIndex + 1;
        if (nextPartIndex >= state.parts.length || state.preloadedPartIndex === nextPartIndex) return;
        const logicalTime = Math.max(0, audio.currentTime - part.mediaStart);
        if (part.duration - logicalTime > 20) return;

        this.releaseAudioPreload(state);
        const preloadAudio = new Audio();
        preloadAudio.preload = 'metadata';
        if (state.parts[nextPartIndex].provider === 'google_drive_gateway') preloadAudio.crossOrigin = 'anonymous';
        preloadAudio.src = state.parts[nextPartIndex].src;
        state.preloadAudio = preloadAudio;
        state.preloadedPartIndex = nextPartIndex;
        preloadAudio.load();
      }

      releaseAudioPreload(state) {
        if (!state || !state.preloadAudio) return;
        state.preloadAudio.removeAttribute('src');
        state.preloadAudio.load();
        state.preloadAudio = null;
        state.preloadedPartIndex = -1;
      }

      updateAudioUI(audio) {
        const scenario = audio.closest('[data-scenario-panel]');
        const state = this.audioStates.get(audio);
        if (!scenario || !state) return;

        const player = scenario.querySelector('[data-player]');
        const progress = scenario.querySelector('[data-audio-progress]');
        const currentTime = scenario.querySelector('[data-current-time]');
        const durationTime = scenario.querySelector('[data-duration-time]');
        const playButton = scenario.querySelector('[data-audio-action="toggle"]');
        const duration = state.duration;
        const current = state.globalTime;
        const isScrubbing = progress && progress.dataset.scrubbing === 'true';
        const isLoading = scenario.classList.contains('is-audio-loading');

        if (progress && !isScrubbing) this.updateProgressControl(progress, current, duration);
        if (currentTime && !isScrubbing) currentTime.textContent = this.formatTime(current);
        if (durationTime) durationTime.textContent = this.formatTime(duration);
        if (player) player.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        if (playButton) {
          const playLabel = isLoading && state.playIntent
            ? 'Loading sample recording'
            : state.playIntent
              ? 'Pause sample recording'
              : state.completed ? 'Replay sample recording' : 'Play sample recording';
          playButton.setAttribute('aria-label', playLabel);
          playButton.title = playLabel;
        }

        scenario.classList.toggle('is-playing', state.playIntent && !state.completed);
        if (!isScrubbing) this.updateCurrentSegment(audio, current);
      }

      updateProgressControl(progress, current, duration) {
        const safeDuration = duration > 0 ? duration : 1;
        const safeCurrent = Math.max(0, Math.min(current, safeDuration));
        const percentage = safeDuration > 0 ? (safeCurrent / safeDuration) * 100 : 0;
        progress.max = String(safeDuration);
        progress.value = String(safeCurrent);
        progress.style.setProperty('--wwid-progress-percent', `${percentage}%`);
        const player = progress.closest('[data-player]');
        if (player) player.style.setProperty('--wwid-progress-percent', `${percentage}%`);
        progress.setAttribute('aria-valuetext', `${this.formatTime(safeCurrent)} of ${this.formatTime(duration)}`);
      }

      updateScrubPreview(audio, progress) {
        const scenario = audio.closest('[data-scenario-panel]');
        if (!scenario) return;

        const previewTime = Number(progress.value);
        const duration = this.audioDuration(audio);
        const currentTime = scenario.querySelector('[data-current-time]');
        this.updateProgressControl(progress, previewTime, duration);
        if (currentTime) currentTime.textContent = this.formatTime(previewTime);
        this.updateCurrentSegment(audio, previewTime);
      }

      updateCurrentSegment(audio, currentTime) {
        const scenario = audio.closest('[data-scenario-panel]');
        if (!scenario) return;

        const transcriptState = this.getTranscriptState(scenario);
        const starts = transcriptState && transcriptState.phase === 'ready'
          ? transcriptState.starts
          : Array.from(scenario.querySelectorAll('[data-segment]')).map((segment) => Number(segment.dataset.start));
        let low = 0;
        let high = starts.length - 1;
        let activeIndex = -1;
        while (low <= high) {
          const middle = Math.floor((low + high) / 2);
          if (currentTime >= starts[middle]) {
            activeIndex = middle;
            low = middle + 1;
          } else {
            high = middle - 1;
          }
        }
        if (transcriptState && transcriptState.activeIndex === activeIndex) return;
        if (!transcriptState && Number(scenario.dataset.activeSegmentIndex || -1) === activeIndex) return;

        const previousIndex = transcriptState ? transcriptState.activeIndex : Number(scenario.dataset.activeSegmentIndex || -1);
        [previousIndex, activeIndex].forEach((index) => {
          if (index < 0) return;
          const segment = scenario.querySelector(`[data-segment-index="${index}"]`) || scenario.querySelectorAll('[data-segment]')[index];
          if (!segment) return;
          const isCurrent = index === activeIndex;
          const timeButton = segment.querySelector('.wwid__segment-time');
          segment.classList.toggle('is-current', isCurrent);
          if (timeButton) {
            if (isCurrent) timeButton.setAttribute('aria-current', 'time');
            else timeButton.removeAttribute('aria-current');
          }
        });
        if (transcriptState) transcriptState.activeIndex = activeIndex;
        scenario.dataset.activeSegmentIndex = String(activeIndex);
      }

      audioDuration(audio) {
        const state = this.audioStates.get(audio);
        if (state && Number.isFinite(state.duration)) return state.duration;
        const fallback = Number(audio.dataset.durationFallback);
        return Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
      }

      formatTime(value) {
        const secondsTotal = Number.isFinite(Number(value)) ? Math.max(0, Math.floor(Number(value))) : 0;
        const hours = Math.floor(secondsTotal / 3600);
        const minutes = Math.floor((secondsTotal % 3600) / 60);
        const seconds = secondsTotal % 60;
        const paddedSeconds = String(seconds).padStart(2, '0');
        if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
        return `${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
      }

      disableAudio(audio, message) {
        const scenario = audio.closest('[data-scenario-panel]');
        if (!scenario) return;

        scenario.querySelectorAll('[data-audio-action]:not([data-audio-action="retry"]), [data-audio-speed], [data-audio-progress], [data-seek]').forEach((control) => {
          control.disabled = true;
        });
        scenario.classList.remove('is-playing');
        scenario.classList.add('is-audio-error');
        this.setAudioLoading(audio, false);
        const state = this.audioStates.get(audio);
        this.showAudioStatus(audio, `${message || 'Sample audio is unavailable.'} The transcript and summaries remain available.`, Boolean(state && state.parts.length));
      }

      enableAudioControls(audio) {
        const scenario = audio.closest('[data-scenario-panel]');
        const state = this.audioStates.get(audio);
        if (!scenario || !state || !state.parts.length) return;

        scenario.querySelectorAll('[data-audio-action], [data-audio-speed], [data-audio-progress], [data-seek]').forEach((control) => {
          control.disabled = false;
        });
        scenario.classList.remove('is-audio-error');
      }

      retryAudio(audio) {
        const state = this.audioStates.get(audio);
        if (!state || !state.parts.length) return;
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        state.currentPartIndex = -1;
        state.pendingGlobalTime = null;
        state.pendingPlay = false;
        state.playIntent = false;
        state.completed = false;
        this.enableAudioControls(audio);
        this.setAudioLoading(audio, true);
        this.playAudio(audio);
      }

      setAudioLoading(audio, isLoading) {
        const scenario = audio.closest('[data-scenario-panel]');
        const player = scenario ? scenario.querySelector('[data-player]') : null;
        if (!scenario || (isLoading && scenario.classList.contains('is-audio-error'))) return;

        scenario.classList.toggle('is-audio-loading', isLoading);
        if (player) player.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        if (isLoading) {
          this.showAudioStatus(audio, 'Loading sample audio...');
        } else if (!scenario.classList.contains('is-audio-error')) {
          this.hideAudioStatus(audio);
        }
      }

      showAudioStatus(audio, message, canRetry) {
        const scenario = audio.closest('[data-scenario-panel]');
        const status = scenario ? scenario.querySelector('[data-audio-status]') : null;
        if (!status) return;

        const text = status.querySelector('[data-audio-status-text]');
        const retry = status.querySelector('[data-audio-action="retry"]');
        status.dataset.state = canRetry ? 'error' : 'message';
        if (text) text.textContent = message;
        if (retry) retry.hidden = !canRetry;
      }

      hideAudioStatus(audio) {
        const scenario = audio.closest('[data-scenario-panel]');
        const status = scenario ? scenario.querySelector('[data-audio-status]') : null;
        if (!status || scenario.classList.contains('is-audio-error')) return;

        const text = status.querySelector('[data-audio-status-text]');
        const retry = status.querySelector('[data-audio-action="retry"]');
        status.dataset.state = 'idle';
        if (text) text.textContent = '';
        if (retry) retry.hidden = true;
      }

      getTranscriptState(scenario) {
        const container = scenario ? scenario.querySelector('[data-transcript-segments][data-transcript-src]') : null;
        if (!container) return null;
        let state = this.transcriptStates.get(container);
        if (state) {
          this.transcriptStateList.add(state);
          return state;
        }

        state = {
          scenario,
          container,
          src: container.dataset.transcriptSrc,
          phase: 'idle',
          segments: [],
          starts: [],
          renderedCount: 0,
          activeIndex: -1,
          abortController: null,
        };
        this.transcriptStates.set(container, state);
        this.transcriptStateList.add(state);
        return state;
      }

      ensureTranscript(scenario) {
        const state = this.getTranscriptState(scenario);
        if (!state || state.phase === 'loading') return;
        if (state.phase === 'ready') {
          if (state.renderedCount === 0) {
            this.renderNextTranscriptBatch(scenario);
          } else {
            this.updateTranscriptObserver(scenario, state.renderedCount < state.segments.length);
          }
          return;
        }
        if (!state.src) return;

        state.phase = 'loading';
        const abortController = new AbortController();
        state.abortController = abortController;
        state.container.setAttribute('aria-busy', 'true');
        this.setTranscriptStatus(scenario, 'Loading transcript...', false);

        fetch(state.src, {
          credentials: 'omit',
          signal: abortController.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Transcript request failed (${response.status}).`);
            const contentLength = Number(response.headers.get('content-length'));
            if (Number.isFinite(contentLength) && contentLength > MAX_TRANSCRIPT_BYTES) {
              throw new Error('Transcript file exceeds the 8 MiB storefront limit.');
            }
            return this.readTranscriptPayload(response);
          })
          .then((payload) => {
            state.segments = this.parseTranscriptPayload(payload, state);
            state.starts = state.segments.map((segment) => segment.start);
            state.phase = 'ready';
            state.renderedCount = 0;
            state.activeIndex = -1;
            state.container.replaceChildren();
            state.container.setAttribute('aria-busy', 'false');
            this.setTranscriptStatus(scenario, '', false);
            const originalButton = scenario.querySelector('[data-open-original]');
            if (originalButton) originalButton.disabled = false;
            this.renderNextTranscriptBatch(scenario);
            const audio = scenario.querySelector('[data-audio]');
            const audioState = audio ? this.audioStates.get(audio) : null;
            if (audio && audioState) this.updateCurrentSegment(audio, audioState.globalTime);
          })
          .catch((error) => {
            if (error && error.name === 'AbortError') {
              if (state.abortController === abortController) {
                state.phase = 'idle';
                state.container.setAttribute('aria-busy', 'false');
              }
              return;
            }
            state.phase = 'error';
            state.container.setAttribute('aria-busy', 'false');
            this.setTranscriptStatus(scenario, 'Transcript could not be loaded. Summary and templates remain available.', true);
          })
          .finally(() => {
            if (state.abortController === abortController) state.abortController = null;
          });
      }

      async readTranscriptPayload(response) {
        if (!response.body || typeof response.body.getReader !== 'function') {
          const text = await response.text();
          if (new TextEncoder().encode(text).byteLength > MAX_TRANSCRIPT_BYTES) {
            throw new Error('Transcript file exceeds the 8 MiB storefront limit.');
          }
          return JSON.parse(text);
        }

        const reader = response.body.getReader();
        const chunks = [];
        let totalBytes = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          totalBytes += value.byteLength;
          if (totalBytes > MAX_TRANSCRIPT_BYTES) {
            await reader.cancel();
            throw new Error('Transcript file exceeds the 8 MiB storefront limit.');
          }
          chunks.push(value);
        }

        const body = new Uint8Array(totalBytes);
        let offset = 0;
        chunks.forEach((chunk) => {
          body.set(chunk, offset);
          offset += chunk.byteLength;
        });
        return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(body));
      }

      parseTranscriptPayload(payload, state) {
        const isLegacyPayload = Array.isArray(payload);
        const source = isLegacyPayload ? payload : payload && payload.segments;
        if (!Array.isArray(source) || !source.length) throw new Error('Transcript has no segments.');
        if (source.length > 5000) throw new Error('Transcript exceeds 5,000 segments.');

        let transcriptDuration = null;
        if (!isLegacyPayload) {
          const expectedScenario = String(state?.scenario?.dataset.scenarioKey || '');
          const expectedVersion = String(state?.scenario?.dataset.contentVersion || '');
          const expectedDurationMs = Number(state?.scenario?.dataset.durationMs);
          const durationMs = payload.durationMs;
          if (
            payload.schemaVersion !== 1
            || payload.timeUnit !== 'milliseconds'
            || typeof payload.scenario !== 'string'
            || payload.scenario !== expectedScenario
            || !expectedScenario
            || typeof payload.contentVersion !== 'string'
            || payload.contentVersion !== expectedVersion
            || !expectedVersion
            || !Number.isSafeInteger(durationMs)
            || durationMs <= 0
            || !Number.isSafeInteger(expectedDurationMs)
            || Math.abs(durationMs - expectedDurationMs) > 2
          ) {
            throw new Error('Transcript metadata does not match this recording.');
          }
          transcriptDuration = durationMs / 1000;
        }

        let previousStart = -1;
        return source.map((segment, index) => {
          const startValue = isLegacyPayload ? segment.startMs ?? segment.start_ms : segment.startMs;
          const endValue = isLegacyPayload ? segment.endMs ?? segment.end_ms : segment.endMs;
          const start = startValue !== undefined ? Number(startValue) / 1000 : Number(segment.start);
          const end = endValue !== undefined ? Number(endValue) / 1000 : Number(segment.end);
          const speakerValue = isLegacyPayload
            ? segment.speaker ?? segment.speakerLabel ?? segment.speaker_label ?? ''
            : segment.speaker;
          const originalValue = isLegacyPayload
            ? segment.original ?? segment.originalText ?? segment.original_text ?? segment.text ?? ''
            : segment.original;
          const optimizedValue = isLegacyPayload
            ? segment.optimized ?? segment.optimizedText ?? segment.optimized_text ?? originalValue
            : segment.optimized;
          const canonicalTypesAreValid = isLegacyPayload || (
            Number.isSafeInteger(startValue)
            && Number.isSafeInteger(endValue)
            && typeof speakerValue === 'string'
            && typeof originalValue === 'string'
            && typeof optimizedValue === 'string'
          );
          const speaker = String(speakerValue ?? '').trim();
          const original = String(originalValue ?? '').trim();
          const optimized = String(optimizedValue ?? original).trim();
          if (
            !Number.isFinite(start)
            || start < 0
            || start < previousStart
            || (!optimized && !original)
            || !canonicalTypesAreValid
            || (!isLegacyPayload && (!speaker || !original || !optimized))
          ) {
            throw new Error(`Transcript segment ${index + 1} is invalid.`);
          }
          if (Number.isFinite(end) && end < start) throw new Error(`Transcript segment ${index + 1} ends before it starts.`);
          if (transcriptDuration !== null && (start > transcriptDuration || !Number.isFinite(end) || end > transcriptDuration)) {
            throw new Error(`Transcript segment ${index + 1} is outside the recording duration.`);
          }
          if (optimized.length > 20000 || original.length > 20000 || speaker.length > 200) {
            throw new Error(`Transcript segment ${index + 1} exceeds the text limit.`);
          }
          previousStart = start;
          return {
            start,
            end: Number.isFinite(end) ? end : null,
            speaker,
            optimized: optimized || original,
            original: original || optimized,
          };
        });
      }

      setTranscriptStatus(scenario, message, canRetry) {
        const status = scenario ? scenario.querySelector('[data-transcript-status]') : null;
        if (!status) return;
        const text = status.querySelector('[data-transcript-status-text]');
        const retry = status.querySelector('[data-transcript-retry]');
        status.hidden = !message;
        status.dataset.state = canRetry ? 'error' : message ? 'loading' : 'idle';
        if (text) text.textContent = message;
        if (retry) retry.hidden = !canRetry;
      }

      retryTranscript(scenario) {
        const state = this.getTranscriptState(scenario);
        if (!state) return;
        if (state.abortController) state.abortController.abort();
        state.phase = 'idle';
        state.segments = [];
        state.starts = [];
        state.renderedCount = 0;
        state.activeIndex = -1;
        state.container.replaceChildren();
        this.ensureTranscript(scenario);
      }

      renderNextTranscriptBatch(scenario) {
        const state = this.getTranscriptState(scenario);
        if (!state || state.phase !== 'ready') return;
        const nextCount = Math.min(state.segments.length, state.renderedCount + TRANSCRIPT_BATCH_SIZE);
        const fragment = document.createDocumentFragment();
        const audio = scenario.querySelector('[data-audio]');
        const audioState = audio ? this.audioStates.get(audio) : null;
        const audioAvailable = Boolean(audioState && audioState.parts.length && !scenario.classList.contains('is-audio-error'));

        for (let index = state.renderedCount; index < nextCount; index += 1) {
          fragment.appendChild(this.createTranscriptSegment(state.segments[index], index, audioAvailable, state.activeIndex === index));
        }
        state.container.appendChild(fragment);
        state.renderedCount = nextCount;

        const pagination = scenario.querySelector('[data-transcript-pagination]');
        const count = pagination ? pagination.querySelector('[data-transcript-count]') : null;
        const hasMore = state.renderedCount < state.segments.length;
        if (pagination) pagination.hidden = !hasMore;
        if (count) count.textContent = `Showing ${state.renderedCount} of ${state.segments.length}`;
        this.updateTranscriptObserver(scenario, hasMore);
      }

      createTranscriptSegment(segment, index, audioAvailable, isCurrent) {
        const article = document.createElement('article');
        article.className = `wwid__segment${isCurrent ? ' is-current' : ''}`;
        article.dataset.segment = '';
        article.dataset.segmentIndex = String(index);
        article.dataset.start = String(segment.start);

        const meta = document.createElement('div');
        meta.className = 'wwid__segment-meta';
        const timeButton = document.createElement('button');
        timeButton.type = 'button';
        timeButton.className = 'wwid__segment-time';
        timeButton.dataset.seek = String(segment.start);
        timeButton.setAttribute('aria-label', `Play from ${this.formatTime(segment.start)}`);
        timeButton.disabled = !audioAvailable;
        if (isCurrent) timeButton.setAttribute('aria-current', 'time');

        const time = document.createElement('time');
        time.dateTime = `PT${segment.start}S`;
        time.textContent = this.formatTime(segment.start);
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('aria-hidden', 'true');
        const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttribute('href', `#wwid-play-${this.dataset.sectionId}`);
        icon.appendChild(use);
        timeButton.append(time, icon);
        meta.appendChild(timeButton);

        if (segment.speaker) {
          const speaker = document.createElement('span');
          speaker.className = 'wwid__speaker';
          speaker.textContent = segment.speaker;
          meta.appendChild(speaker);
        }

        const copy = document.createElement('p');
        copy.textContent = segment.optimized;
        article.append(meta, copy);
        return article;
      }

      updateTranscriptObserver(scenario, hasMore) {
        const existing = this.transcriptObservers.get(scenario);
        if (existing && !hasMore) {
          existing.disconnect();
          this.transcriptObservers.delete(scenario);
          return;
        }
        if (!hasMore || existing || !('IntersectionObserver' in window)) return;
        const pagination = scenario.querySelector('[data-transcript-pagination]');
        if (!pagination) return;
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && scenario.classList.contains('is-active')) {
            this.renderNextTranscriptBatch(scenario);
          }
        }, { rootMargin: '160px 0px' });
        observer.observe(pagination);
        this.transcriptObservers.set(scenario, observer);
      }

      openOriginalTranscript(trigger) {
        if (this.isModalOpen()) return;
        const scenario = trigger.closest('[data-scenario-panel]');
        const transcriptState = this.getTranscriptState(scenario);
        if (transcriptState && transcriptState.phase === 'ready') {
          const content = document.createElement('div');
          content.className = 'wwid__original-transcript';
          const list = document.createElement('div');
          const loadMore = document.createElement('button');
          loadMore.type = 'button';
          loadMore.className = 'wwid__original-load-more';
          loadMore.textContent = 'Load more transcript';
          content.append(list, loadMore);
          let renderedCount = 0;
          const renderBatch = () => {
            const nextCount = Math.min(transcriptState.segments.length, renderedCount + TRANSCRIPT_BATCH_SIZE);
            const fragment = document.createDocumentFragment();
            for (let index = renderedCount; index < nextCount; index += 1) {
              const segment = transcriptState.segments[index];
              const article = document.createElement('article');
              article.className = 'wwid__original-segment';
              const meta = document.createElement('div');
              const time = document.createElement('time');
              time.dateTime = `PT${segment.start}S`;
              time.textContent = this.formatTime(segment.start);
              meta.appendChild(time);
              if (segment.speaker) {
                const speaker = document.createElement('strong');
                speaker.textContent = segment.speaker;
                meta.appendChild(speaker);
              }
              const copy = document.createElement('p');
              copy.textContent = segment.original;
              article.append(meta, copy);
              fragment.appendChild(article);
            }
            list.appendChild(fragment);
            renderedCount = nextCount;
            loadMore.hidden = renderedCount >= transcriptState.segments.length;
          };
          renderBatch();
          this.openModal(content, 'Original transcript', trigger, (modalBox) => {
            const modalLoadMore = modalBox.querySelector('.wwid__original-load-more');
            if (!modalLoadMore) return;
            modalLoadMore.addEventListener('click', renderBatch);
            this.addModalCleanup(() => modalLoadMore.removeEventListener('click', renderBatch));
          });
          return;
        }
        const source = scenario ? scenario.querySelector('[data-original-source]') : null;
        const content = source && source.content.firstElementChild ? source.content.firstElementChild.cloneNode(true) : null;
        if (!content) return;

        this.openModal(content, 'Original transcript', trigger, null);
      }

      openTemplatePicker(trigger, mode) {
        if (this.isModalOpen()) return;
        const scenario = trigger.closest('[data-scenario-panel]');
        const source = scenario ? scenario.querySelector('[data-template-picker-source]') : null;
        const content = source && source.content.firstElementChild ? source.content.firstElementChild.cloneNode(true) : null;
        if (!content) return;

        const targetGroup = trigger.closest('[data-template-tab]') || (scenario ? scenario.querySelector('[data-template-tab].is-active:not([hidden])') : null);
        const focusReturn = mode === 'replace' && targetGroup
          ? targetGroup.querySelector('[data-output-menu-trigger]')
          : trigger;
        content.dataset.pickerMode = mode;
        content.dataset.replaceOutputKey = targetGroup ? targetGroup.dataset.outputTabGroup : '';
        const modalTitle = mode === 'add' ? 'Choose a template' : 'Replace a template';
        this.openModal(content, modalTitle, focusReturn || trigger, (modalBox) => {
          this.bindTemplatePicker(modalBox, scenario);
        });
      }

      ensurePopupRoot() {
        if (window.root && window.root.style) return;
        // PopupBase can run before theme.js creates its global root on WebKit.
        window.root = document.body;
      }

      openModal(content, title, trigger, afterOpen) {
        if (this.focusRestoreTimer) window.clearTimeout(this.focusRestoreTimer);
        this.focusRestoreTimer = null;
        this.modalTrigger = trigger;
        this.modalSequence += 1;
        const modalSequence = this.modalSequence;
        const titleId = `wwid-modal-title-${this.dataset.sectionId}-${this.modalSequence}`;
        const closeLabel = `Close ${title.toLowerCase()}`;
        const titleMarkup = `
          <div class="wwid__modal-header">
            <h2 class="wwid__modal-title" id="${titleId}">${title}</h2>
            <button class="wwid__modal-close" type="button" aria-label="${closeLabel}" title="${closeLabel}" data-close-demo-modal>
              <svg aria-hidden="true"><use href="#icon-close"></use></svg>
            </button>
          </div>
        `;

        this.ensurePopupRoot();
        this.initPopup(content, titleMarkup, 'ww-intelligence-demo-modal', (modalBox) => {
          modalBox.setAttribute('role', 'dialog');
          modalBox.setAttribute('aria-modal', 'true');
          modalBox.setAttribute('aria-labelledby', titleId);
          const modalRoot = modalBox.closest('.tingle-modal');
          const defaultClose = modalRoot ? modalRoot.querySelector('.tingle-modal__close') : null;
          const closeButton = modalBox.querySelector('[data-close-demo-modal]');
          if (defaultClose) {
            defaultClose.hidden = true;
            defaultClose.disabled = true;
            defaultClose.tabIndex = -1;
          }
          if (closeButton) {
            const closeHandler = () => this.requestClose();
            closeButton.addEventListener('click', closeHandler);
            this.addModalCleanup(() => closeButton.removeEventListener('click', closeHandler));
          }
          if (afterOpen) afterOpen(modalBox);
          if (this._focusTrapCleanup) this._focusTrapCleanup();
          this._focusTrapCleanup = this.trapVisibleFocus(modalBox);
          const picker = modalBox.querySelector('[data-template-picker]');
          const preferredFocus = picker && picker.dataset.pickerMode === 'replace-select'
            ? modalBox.querySelector('[data-template-replace-option]')
            : modalBox.querySelector('[data-template-search]');
          if (preferredFocus) preferredFocus.focus();
        });

        const activeModal = this.modal;
        if (!activeModal) return;
        const nativeClose = activeModal.close.bind(activeModal);
        activeModal._wwidSequence = modalSequence;
        activeModal._wwidClosing = false;
        activeModal.close = () => {
          if (activeModal._wwidClosing || !activeModal.modal) return;
          activeModal._wwidClosing = true;
          nativeClose();
        };
      }

      bindTemplatePicker(modalBox, scenario) {
        const picker = modalBox.querySelector('[data-template-picker]');
        if (!picker || !scenario) return;

        this.prepareTemplatePicker(picker, scenario);

        const clickHandler = (event) => {
          const category = event.target.closest('[data-template-category]');
          const option = event.target.closest('[data-template-option]');
          const replaceOption = event.target.closest('[data-template-replace-option]');
          const confirmReplace = event.target.closest('[data-confirm-replace-target]');
          const commit = event.target.closest('[data-commit-template]');
          const cancel = event.target.closest('[data-close-template-picker]');

          if (replaceOption) {
            this.selectReplaceTarget(picker, replaceOption);
            return;
          }

          if (confirmReplace) {
            const selectedTarget = picker.querySelector('[data-template-replace-option][aria-checked="true"]');
            if (!selectedTarget) return;
            picker.dataset.pickerMode = 'replace';
            picker.dataset.replaceOutputKey = selectedTarget.dataset.replaceOutputKey || '';
            const replaceStep = picker.querySelector('[data-template-replace-step]');
            const browser = picker.querySelector('[data-template-browser]');
            if (replaceStep) replaceStep.hidden = true;
            if (browser) browser.hidden = false;
            this.prepareTemplatePicker(picker, scenario);
            const search = picker.querySelector('[data-template-search]');
            if (search) search.focus();
            return;
          }

          if (category) {
            picker.querySelectorAll('[data-template-category]').forEach((button) => {
              const isActive = button === category;
              button.classList.toggle('is-active', isActive);
              button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });
            picker.dataset.activeCategory = category.dataset.templateCategory;
            this.filterTemplates(picker, true);
            return;
          }

          if (option) {
            this.selectTemplateOption(picker, option);
            return;
          }

          if (commit) {
            const selected = picker.querySelector('[data-template-option][aria-checked="true"]');
            if (!selected) return;
            const outputKey = selected.dataset.templateOption;
            const selectedName = selected.querySelector('strong');
            const outputTab = this.commitTemplateSelection(
              scenario,
              outputKey,
              picker.dataset.pickerMode || 'add',
              picker.dataset.replaceOutputKey || ''
            );
            if (!outputTab) return;
            this.modalTrigger = outputTab;
            const action = picker.dataset.pickerMode === 'replace' ? 'replaced' : 'added';
            this.announce(`${selectedName ? selectedName.textContent : 'Template'} template ${action}.`);
            this.closeActiveModal();
            return;
          }

          if (cancel) this.closeActiveModal();
        };

        const inputHandler = (event) => {
          if (!event.target.matches('[data-template-search]')) return;
          this.filterTemplates(picker, true);
        };

        const keyHandler = (event) => {
          const option = event.target.closest('[data-template-option], [data-template-replace-option]');
          if (!option || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          const optionSelector = option.hasAttribute('data-template-replace-option')
            ? '[data-template-replace-option]:not([hidden])'
            : '[data-template-option]:not([hidden])';
          const visibleOptions = Array.from(picker.querySelectorAll(optionSelector));
          const currentIndex = visibleOptions.indexOf(option);
          if (currentIndex < 0 || visibleOptions.length < 2) return;
          const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          const nextIndex = forward ? (currentIndex + 1) % visibleOptions.length : (currentIndex - 1 + visibleOptions.length) % visibleOptions.length;
          event.preventDefault();
          if (option.hasAttribute('data-template-replace-option')) {
            this.selectReplaceTarget(picker, visibleOptions[nextIndex]);
          } else {
            this.selectTemplateOption(picker, visibleOptions[nextIndex]);
          }
          visibleOptions[nextIndex].focus();
        };

        picker.addEventListener('click', clickHandler);
        picker.addEventListener('input', inputHandler);
        picker.addEventListener('keydown', keyHandler);
        this.addModalCleanup(() => {
          picker.removeEventListener('click', clickHandler);
          picker.removeEventListener('input', inputHandler);
          picker.removeEventListener('keydown', keyHandler);
        });
      }

      prepareTemplatePicker(picker, scenario) {
        const mode = picker.dataset.pickerMode || 'add';
        const replaceStep = picker.querySelector('[data-template-replace-step]');
        const browser = picker.querySelector('[data-template-browser]');
        const commit = picker.querySelector('[data-commit-template]');

        if (mode === 'replace-select') {
          this.buildReplaceTargetOptions(picker, scenario);
          return;
        }

        if (replaceStep) replaceStep.hidden = true;
        if (browser) browser.hidden = false;
        if (commit) commit.textContent = mode === 'replace' ? 'Replace template' : 'Add template';
        const installedKeys = new Set(
          Array.from(scenario.querySelectorAll('[data-template-tab]:not([hidden])')).map((group) => group.dataset.outputTabGroup)
        );

        picker.querySelectorAll('[data-template-option]').forEach((option) => {
          const outputKey = option.dataset.templateOption;
          const isAvailable = !installedKeys.has(outputKey);
          option.dataset.templateAvailable = isAvailable ? 'true' : 'false';
        });

        picker.querySelectorAll('[data-template-category]:not([data-template-category="all"])').forEach((category) => {
          const categoryValue = category.dataset.templateCategory;
          const hasAvailableOption = Array.from(picker.querySelectorAll('[data-template-option]')).some((option) => {
            return option.dataset.templateAvailable !== 'false' && option.dataset.templateCategoryValue === categoryValue;
          });
          category.hidden = !hasAvailableOption;
        });

        this.filterTemplates(picker, false);
      }

      buildReplaceTargetOptions(picker, scenario) {
        const replaceStep = picker.querySelector('[data-template-replace-step]');
        const browser = picker.querySelector('[data-template-browser]');
        const options = picker.querySelector('[data-template-replace-options]');
        const confirm = picker.querySelector('[data-confirm-replace-target]');
        if (!replaceStep || !browser || !options || !confirm) return;

        options.replaceChildren();
        Array.from(scenario.querySelectorAll('[data-template-tab]:not([hidden])')).forEach((group, index) => {
          const tab = group.querySelector('[data-output-tab]');
          if (!tab) return;
          const option = document.createElement('button');
          option.type = 'button';
          option.className = 'wwid__replace-option';
          option.setAttribute('role', 'radio');
          option.setAttribute('aria-checked', 'false');
          option.tabIndex = index === 0 ? 0 : -1;
          option.dataset.templateReplaceOption = '';
          option.dataset.replaceOutputKey = group.dataset.outputTabGroup || '';
          option.textContent = tab.textContent.trim();
          options.appendChild(option);
        });

        confirm.disabled = true;
        confirm.setAttribute('aria-disabled', 'true');
        browser.hidden = true;
        replaceStep.hidden = false;
      }

      selectReplaceTarget(picker, option) {
        picker.querySelectorAll('[data-template-replace-option]').forEach((item) => {
          const isSelected = item === option;
          item.classList.toggle('is-selected', isSelected);
          item.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          item.tabIndex = isSelected ? 0 : -1;
        });
        const confirm = picker.querySelector('[data-confirm-replace-target]');
        if (confirm) {
          confirm.disabled = false;
          confirm.setAttribute('aria-disabled', 'false');
        }
      }

      selectTemplateOption(picker, option) {
        picker.querySelectorAll('[data-template-option]').forEach((card) => {
          const isSelected = card === option;
          card.classList.toggle('is-selected', isSelected);
          card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          card.tabIndex = isSelected ? 0 : -1;
        });
        const commit = picker.querySelector('[data-commit-template]');
        if (commit) {
          commit.disabled = false;
          commit.setAttribute('aria-disabled', 'false');
        }
      }

      filterTemplates(picker, announceResults) {
        const search = picker.querySelector('[data-template-search]');
        const activeCategoryButton = picker.querySelector('[data-template-category][aria-pressed="true"]');
        const query = search ? search.value.trim().toLowerCase() : '';
        const activeCategory = activeCategoryButton ? activeCategoryButton.dataset.templateCategory : 'all';
        let visibleCount = 0;

        picker.querySelectorAll('[data-template-option]').forEach((option) => {
          const matchesSearch = !query || option.dataset.templateSearchValue.includes(query);
          const matchesCategory = activeCategory === 'all' || option.dataset.templateCategoryValue === activeCategory;
          const isAvailable = option.dataset.templateAvailable !== 'false';
          const isVisible = isAvailable && matchesSearch && matchesCategory;
          option.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
          if (!isVisible && option.getAttribute('aria-checked') === 'true') {
            option.classList.remove('is-selected');
            option.setAttribute('aria-checked', 'false');
          }
        });

        const selected = picker.querySelector('[data-template-option][aria-checked="true"]');
        const firstVisible = picker.querySelector('[data-template-option]:not([hidden])');
        picker.querySelectorAll('[data-template-option]').forEach((option) => {
          option.tabIndex = option === selected || (!selected && option === firstVisible) ? 0 : -1;
        });
        const commit = picker.querySelector('[data-commit-template]');
        const empty = picker.querySelector('[data-template-empty]');
        if (commit) {
          commit.disabled = !selected;
          commit.setAttribute('aria-disabled', selected ? 'false' : 'true');
        }
        if (empty) empty.hidden = visibleCount > 0;
        if (announceResults) {
          if (this.templateFilterAnnouncementTimer) window.clearTimeout(this.templateFilterAnnouncementTimer);
          this.templateFilterAnnouncementTimer = window.setTimeout(() => {
            const modalAnnouncer = picker.querySelector('[data-template-announcer]');
            const message = `${visibleCount} ${visibleCount === 1 ? 'template' : 'templates'} available.`;
            if (modalAnnouncer) {
              modalAnnouncer.textContent = message;
            } else {
              this.announce(message);
            }
            this.templateFilterAnnouncementTimer = null;
          }, 200);
        }
      }

      trapVisibleFocus(container) {
        const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const getFocusable = () => Array.from(container.querySelectorAll(selector)).filter((element) => {
          return !element.hidden && !element.closest('[hidden]') && element.getClientRects().length > 0;
        });
        const first = getFocusable()[0];
        if (first) first.focus();

        const handleTab = (event) => {
          if (event.key !== 'Tab') return;
          const focusable = getFocusable();
          if (!focusable.length) return;
          const firstVisible = focusable[0];
          const lastVisible = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === firstVisible) {
            event.preventDefault();
            lastVisible.focus();
          } else if (!event.shiftKey && document.activeElement === lastVisible) {
            event.preventDefault();
            firstVisible.focus();
          }
        };

        container.addEventListener('keydown', handleTab);
        return () => container.removeEventListener('keydown', handleTab);
      }

      isModalOpen() {
        return Boolean(this.modal && this.modal.modal && this.modal.modal.classList.contains('tingle-modal--visible'));
      }

      closeActiveModal() {
        this.requestClose();
      }

      requestClose() {
        if (!this.modal || !this.modal.modal || typeof this.modal.close !== 'function') return;
        this.modal.close();
      }

      addModalCleanup(cleanup) {
        if (typeof cleanup === 'function') this.modalCleanups.push(cleanup);
      }

      cleanupModalBindings() {
        this.modalCleanups.splice(0).reverse().forEach((cleanup) => cleanup());
      }

      onCloseEvent() {
        const trigger = this.modalTrigger;
        const closedModal = this.modal;
        const closingSequence = closedModal && closedModal._wwidSequence
          ? closedModal._wwidSequence
          : this.modalSequence;
        const modalRoot = closedModal ? closedModal.modal : null;
        this.modalTrigger = null;
        if (this.templateFilterAnnouncementTimer) window.clearTimeout(this.templateFilterAnnouncementTimer);
        this.templateFilterAnnouncementTimer = null;
        this.cleanupModalBindings();

        if (this.modalRemovalObserver) this.modalRemovalObserver.disconnect();
        if (modalRoot && modalRoot.parentNode) {
          this.modalRemovalObserver = new MutationObserver(() => {
            if (modalRoot.isConnected) return;
            this.modalRemovalObserver.disconnect();
            this.modalRemovalObserver = null;
            if (this.modal === closedModal) this.modal = null;
          });
          this.modalRemovalObserver.observe(modalRoot.parentNode, { childList: true });
        } else if (this.modal === closedModal) {
          this.modal = null;
        }

        if (this.focusRestoreTimer) window.clearTimeout(this.focusRestoreTimer);
        this.focusRestoreTimer = window.setTimeout(() => {
          this.focusRestoreTimer = null;
          if (this.modalSequence !== closingSequence) return;
          if (trigger && trigger.isConnected) trigger.focus();
        }, 300);
      }

      buildOutlines() {
        this.summaryPanels.forEach((panel) => {
          const content = panel.querySelector('[data-summary-content]');
          const outline = panel.querySelector('[data-summary-outline]');
          if (!content || !outline) return;
          outline.replaceChildren();

          const headings = Array.from(content.querySelectorAll('p')).filter((element) => {
            return element.tagName === 'P' && element.firstElementChild && element.firstElementChild.tagName === 'STRONG';
          });

          headings.forEach((heading, index) => {
            heading.classList.add('wwid__summary-heading');
            heading.id = `${panel.id}-section-${index}`;
            const marker = document.createElement('button');
            marker.type = 'button';
            marker.className = 'wwid__outline-marker';
            marker.dataset.outlineTarget = heading.id;
            const markerText = heading.firstElementChild.textContent.trim();
            const markerLabel = document.createElement('span');
            markerLabel.className = 'wwid__outline-label';
            markerLabel.textContent = markerText;
            marker.appendChild(markerLabel);
            marker.title = markerText;
            marker.setAttribute('aria-label', `Go to ${markerText}`);
            outline.appendChild(marker);
          });

          outline.hidden = headings.length < 2;
          this.updateOutline(panel);
        });
      }

      handleSummaryScroll(event) {
        this.updateOutline(event.currentTarget);
      }

      updateOutline(panel) {
        if (!panel || panel.hidden) return;
        const headings = Array.from(panel.querySelectorAll('.wwid__summary-heading'));
        const markers = Array.from(panel.querySelectorAll('[data-outline-target]'));
        if (!headings.length || !markers.length) return;

        let activeIndex = 0;
        const panelRect = panel.getBoundingClientRect();
        const usesPageScroll = window.getComputedStyle(panel).overflowY === 'visible';
        const outputBar = usesPageScroll
          ? panel.closest('[data-scenario-panel]')?.querySelector('.wwid__output-bar')
          : null;
        const activationLine = usesPageScroll
          ? Math.max(0, outputBar ? outputBar.getBoundingClientRect().bottom : 0) + 24
          : panelRect.top + 48;
        headings.forEach((heading, index) => {
          if (heading.getBoundingClientRect().top <= activationLine + 1) activeIndex = index;
        });
        if (!usesPageScroll && panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2) {
          activeIndex = headings.length - 1;
        }

        const previousActiveIndex = markers.findIndex((marker) => marker.classList.contains('is-active'));
        markers.forEach((marker, index) => {
          const isActive = index === activeIndex;
          marker.classList.toggle('is-active', isActive);
          if (isActive) {
            marker.setAttribute('aria-current', 'location');
          } else {
            marker.removeAttribute('aria-current');
          }
        });

        if (activeIndex === previousActiveIndex) return;
        const activeMarker = markers[activeIndex];
        const outline = activeMarker ? activeMarker.closest('[data-summary-outline]') : null;
        if (!outline || typeof outline.scrollTo !== 'function') return;

        const outlineRect = outline.getBoundingClientRect();
        const markerRect = activeMarker.getBoundingClientRect();
        let nextLeft = outline.scrollLeft;
        let nextTop = outline.scrollTop;
        if (markerRect.left < outlineRect.left) nextLeft -= outlineRect.left - markerRect.left;
        if (markerRect.right > outlineRect.right) nextLeft += markerRect.right - outlineRect.right;
        if (markerRect.top < outlineRect.top) nextTop -= outlineRect.top - markerRect.top;
        if (markerRect.bottom > outlineRect.bottom) nextTop += markerRect.bottom - outlineRect.bottom;
        if (nextLeft !== outline.scrollLeft || nextTop !== outline.scrollTop) {
          outline.scrollTo({ left: nextLeft, top: nextTop, behavior: 'auto' });
        }
      }

      scrollToSummarySection(trigger) {
        const panel = trigger.closest('.wwid__summary-panel');
        const target = panel ? panel.querySelector(`[id="${trigger.dataset.outlineTarget}"]`) : null;
        if (!panel || !target) return;
        const behavior = this.reducedMotion() ? 'auto' : 'smooth';
        const usesPageScroll = window.getComputedStyle(panel).overflowY === 'visible';
        if (!usesPageScroll && panel.scrollHeight > panel.clientHeight + 1) {
          const panelRect = panel.getBoundingClientRect();
          const targetTop = panel.scrollTop + target.getBoundingClientRect().top - panelRect.top;
          panel.scrollTo({ top: Math.max(0, targetTop - 48), behavior });
        } else {
          const outputBar = panel.closest('[data-scenario-panel]')?.querySelector('.wwid__output-bar');
          const outputBarStyle = outputBar ? window.getComputedStyle(outputBar) : null;
          const outputBarTop = outputBarStyle ? Number.parseFloat(outputBarStyle.top) : 0;
          const stickyStackHeight = outputBar && outputBarStyle.position === 'sticky'
            ? Math.max(0, Number.isFinite(outputBarTop) ? outputBarTop : 0) + outputBar.getBoundingClientRect().height
            : 0;
          const targetTop = window.scrollY + target.getBoundingClientRect().top - stickyStackHeight - 24;
          window.scrollTo({ top: Math.max(0, targetTop), behavior });
        }
      }

      announce(message) {
        const announcer = this.querySelector('[data-demo-announcer]');
        if (announcer) announcer.textContent = message;
      }

      reducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }

    customElements.define('plaud-intelligence-demo', PlaudIntelligenceDemo);
  }

  if (customElements.get('slide-section')) {
    defineIntelligenceDemo();
  } else {
    customElements.whenDefined('slide-section').then(defineIntelligenceDemo);
  }
})();
